/* eslint-disable */
import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import styles from '../Skillset/skillset.module.scss';
import EditIcon from '../../../../../Assets/images/svg/dar_pencil.svg';
import DisabledEditIcon from '../../../../../Assets/images/svg/disabledEdit.svg';

import NoDataFound from '../../../../../Assets/images/svg/dar_no_new_file.svg';
import MagnifyingGlass from '../../../../../Assets/images/svg/dar_magnifying.svg';
import DataGridComponent from '../../../Dashboard/Efforts/scopeDataGrid';
import { TextField } from '@mui/material';
import collapseIcon from '../../../../../Assets/images/svg/dar_purple_dropdown.svg';
import SearchDropdown from '../../../../../Components/Dropdown/dropdown';
import UseAxios from '../../../../../Api/http/http';
import {
  getSkillsetData,
  putSkillsetData,
} from '../../../../../Api/request/dealEffort';
import { useSnackbar } from '../../../../../Components/Snackbar/snackBarProvider';
import { AuthContext } from '../../../../../Contexts/AuthContext';
import { MODULE_NAMES } from '../../../../../Constants/modules';

const Skillset = () => {
  const [searchParams] = useSearchParams();
  const dealId = searchParams.get('id');
  const { snackbarOpen } = useSnackbar();
  const [rows, setRows] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  const { userProfile } = useContext(AuthContext);
  const permission = userProfile?.permissions
    ? userProfile.permissions[MODULE_NAMES.DEAL_TRANSITION_DETAIL_COMMERCIALS] || []
    : [];

    const hasAccess = (action) => {
      return permission.includes(action);
    };

  const handleCollapse = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const locationOptions = [
    { id: 1, name: 'Offsite' },
    { id: 2, name: 'Onsite' },
  ];

  const experienceOptions = [
    { id: 1, name: 'Junior' },
    { id: 2, name: 'Mid' },
    { id: 3, name: 'Senior' },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (isEdit) {
      setIsEdit(false);
    }
  }, [rows]);

  const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };

  const getTaskName = (scope) => {
    switch (scope.type) {
      case 'FE':
        return 'Front End Development';
      case 'BE':
        return 'Back End Development';
      default:
        return scope.task;
    }
  };

  const fetchData = async () => {
    try {
      const getSkillsetValues = await UseAxios(getSkillsetData(dealId));
      const transformedRows = getSkillsetValues?.data?.data?.list?.map(
        (scope, scopeIndex) => ({
          id: scope.scopeId,
          srNo: scopeIndex + 1,
          task: getTaskName(scope),
          items:
            scope.items?.map((item) => ({
              ...item,
              experience: capitalizeFirstLetter(item.experience),
              location: capitalizeFirstLetter(item.location),
              count: parseFloat(item.count),
            })) || [],
          isEditing: false,
        })
      );
      setRows(transformedRows || []);
    } catch (error) {
      console.error('Error fetching data:', error.message || error);
      snackbarOpen('Oops! Please try again', 'warning');
    }
  };

  const handleSearch = (event) => {
    setSearchText(event.target.value.toLowerCase());
  };

  const filteredRows = searchText
    ? rows.filter(
        (row) =>
          row.task?.toLowerCase().includes(searchText.toLowerCase()) ||
          row.items?.some((item) =>
            ['skillset', 'experience', 'location', 'count', 'remarks'].some(
              (field) => {
                const value = item[field];
                return String(value || '')
                  .toLowerCase()
                  .includes(searchText.toLowerCase());
              }
            )
          )
      )
    : rows;

  const updateItemInRow = (row, rowId, itemId, field, value) => {
    if (row.id !== rowId) return row;

    const updatedItems = row.items.map((item) =>
      item.id === itemId ? { ...item, [field]: value } : item
    );

    return { ...row, items: updatedItems };
  };

  const handleInputChange = (e, rowId, itemId, field) => {
    const value = e.target.value?.name || e.target.value;
    setRows((prevRows) =>
      prevRows.map((row) => updateItemInRow(row, rowId, itemId, field, value))
    );
  };

  const handleSave = async (id) => {
    const rowToSave = rows.find((row) => row.id === id);

    const payload = rowToSave.items.map((item) => ({
      deal_skill_id: item.id,
      skillset_id: item.resourceId,
      experience: item.experience,
      location: item.location,
      count: item.count,
      remarks: item.remarks,
    }));

    try {
      await UseAxios(putSkillsetData(id, payload));
      snackbarOpen('Data saved successfully', 'success');
      await fetchData();
      setRows((prevRows) =>
        prevRows.map((row) =>
          row.id === id ? { ...row, isEditing: false, isEdit: false } : row
        )
      );
    } catch {
      console.error('Error saving data:', error.message || error);
      snackbarOpen('Oops! Something went wrong', 'warning');
    }
  };

  const resetEditingRow = (row) => {
    return {
      ...row.originalData,
      isEditing: false,
      isEdit: false,
    };
  };

  const filterNumericIds = (row) => {
    const filteredItems = row.items.filter(
      (item) => typeof item.id === 'number'
    );
    return {
      ...row,
      items: filteredItems,
      isEditing: false,
    };
  };

  const updateRow = (row, id) => {
    if (row.id !== id) return row;

    if (row.isEdit) {
      return resetEditingRow(row);
    }
    return filterNumericIds(row);
  };

  const handleCancel = (id) => {
    setRows((prevRows) => prevRows.map((row) => updateRow(row, id)));
  };

  const handleEdit = (id) => {
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id === id
          ? { ...row, isEditing: true, isEdit: true, originalData: { ...row } }
          : row
      )
    );
  };

  const isRowUnchanged = (row) => {
    return row.items.every((currentItem) => {
      const originalItem = row.originalData.items.find(
        (item) => item.id === currentItem.id
      );
      if (!originalItem) return false;

      return (
        currentItem.experience === originalItem.experience &&
        currentItem.location === originalItem.location &&
        currentItem.count === originalItem.count &&
        currentItem.remarks === originalItem.remarks
      );
    });
  };

  const updateCountValue = (item, itemId, countValue) => {
    if (item.id !== itemId) return item;

    if (countValue === '' || countValue.endsWith('.')) {
      return { ...item, count: countValue };
    }
    const value = parseFloat(countValue);
    if (!isNaN(value) && value >= 0 && value <= 999) {
      return { ...item, count: Math.round(value * 10) / 10 };
    }
    return item;
  };

  const updateRowItems = (row, rowId, itemId, countValue) => {
    if (row.id !== rowId) return row;
    return {
      ...row,
      items: row.items.map((item) =>
        updateCountValue(item, itemId, countValue)
      ),
    };
  };

  const handleCountChange = (countValue, rowId, itemId) => {
    setRows((prevRows) =>
      prevRows.map((row) => updateRowItems(row, rowId, itemId, countValue))
    );
  };

  const updateItemValue = (items, itemId, field, isIncrement) => {
    return items.map((item) => {
      if (item.id !== itemId) return item;
      const currentValue = parseFloat(item[field]);
      const newValue = isIncrement
        ? Math.min(currentValue + 1, 999)
        : Math.max(currentValue - 1, 0.1);
      return { ...item, [field]: Math.round(newValue * 10) / 10 };
    });
  };

  const handleDecrement = (rowId, itemId, field) => {
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id === rowId
          ? { ...row, items: updateItemValue(row.items, itemId, field, false) }
          : row
      )
    );
  };

  const handleIncrement = (rowId, itemId, field) => {
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id === rowId
          ? { ...row, items: updateItemValue(row.items, itemId, field, true) }
          : row
      )
    );
  };

  const getSelectedValue = (name, options) => {
    return options.find((option) => option.name === name) || null;
  };

  const getDisplayItems = (row, expandedRows) => {
    if (!row.items.length) return [];

    return expandedRows[row.id] ? row.items : row.items.slice(0, 1);
  };

  const columns = [
    {
      field: 'srNo',
      headerName: 'Sr. No.',
      minWidth: 90,
      maxWidth: 90,
      sortable: false,
      disableColumnMenu: true,
      flex: 0.1,
      cellClassName: styles.srNoCell,
    },
    {
      field: 'task',
      headerName: 'Task',
      minWidth: 200,
      sortable: false,
      disableColumnMenu: true,
      flex: 0.8,
      renderCell: (params) =>
        params.row.isEditing ? (
          <div className={styles.taskDiv}>
            <div className={styles.saveCancelOuterDiv}>
              <label className={styles.taskLabel}>{params.row.task}</label>
            </div>
          </div>
        ) : (
          <label className="saved-value">{params.row.task}</label>
        ),
    },
    {
      field: 'skillset',
      headerName: 'Role',
      minWidth: 145,
      flex: 0.4,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) =>
        params.row.isEditing ? (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {params.row.items.map((item) => (
              <div className={styles.skillsetDiv} key={`skillset-${item.id}`}>
                <div className={styles.taskItems}>{item?.skillset || '-'}</div>
              </div>
            ))}
          </div>
        ) : (
          <label className="saved-value">
            {!params.row.items.length
              ? '-'
              : getDisplayItems(params.row, expandedRows).map((item) => (
                  <div
                    className={styles.skillsetDiv}
                    key={`skillset:${item.id}`}
                  >
                    <div className={styles.taskItems}>
                      {item?.skillset || '-'}
                    </div>
                  </div>
                ))}
          </label>
        ),
    },
    {
      field: 'experience',
      headerName: 'Experience',
      minWidth: 105,
      flex: 0.3,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) =>
        params.row.isEditing ? (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {params.row.items.map((item, index) => (
              <SearchDropdown
                key={`experience-${item.id}`}
                searchBox={false}
                placeholder="Select"
                options={experienceOptions}
                onChange={(e) =>
                  handleInputChange(e, params.row.id, item.id, 'experience')
                }
                value={getSelectedValue(item.experience, experienceOptions)}
                dropdownCss={styles.textbox}
              />
            ))}
          </div>
        ) : (
          <label className="saved-value">
            {!params.row.items.length
              ? '-'
              : getDisplayItems(params.row, expandedRows).map((item) => (
                  <div
                    className={styles.taskItems}
                    key={`experience:${item.id}`}
                  >
                    {item?.experience || '-'}
                  </div>
                ))}
          </label>
        ),
    },
    {
      field: 'location',
      headerName: 'Location',
      flex: 0.3,
      minWidth: 105,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) =>
        params.row.isEditing ? (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {params.row.items.map((item) => (
              <SearchDropdown
                key={`location-${item.id}`}
                searchBox={false}
                placeholder="Select"
                options={locationOptions}
                onChange={(e) =>
                  handleInputChange(e, params.row.id, item.id, 'location')
                }
                value={getSelectedValue(item.location, locationOptions)}
                dropdownCss={styles.textbox}
              />
            ))}
          </div>
        ) : (
          <label className="saved-value">
            {!params.row.items.length
              ? '-'
              : getDisplayItems(params.row, expandedRows).map((item) => (
                  <div className={styles.taskItems} key={`location:${item.id}`}>
                    {item?.location || '-'}
                  </div>
                ))}
          </label>
        ),
    },
    {
      field: 'count',
      headerName: 'Count',
      minWidth: 100,
      flex: 0.3,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return params.row.isEditing ? (
          <div>
            {params.row.items.map((item) => (
              <div className={styles.counterDiv} key={`count:${item.id}`}>
                <button
                  style={{ marginBottom: '3px' }}
                  className={styles.counterButtonDec}
                  onClick={() =>
                    handleDecrement(params.row.id, item.id, 'count')
                  }
                >
                  –
                </button>
                <input
                  type="text"
                  value={item.count}
                  onChange={(e) =>
                    handleCountChange(e.target.value, params.row.id, item.id)
                  }
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    const finalValue =
                      value === '0' || value === '0.' || value === ''
                        ? '1'
                        : parseFloat(value).toFixed(1);
                    handleCountChange(finalValue, params.row.id, item.id);
                  }}
                />
                <button
                  className={styles.counterButtonInc}
                  onClick={() =>
                    handleIncrement(params.row.id, item.id, 'count')
                  }
                >
                  +
                </button>
              </div>
            ))}
          </div>
        ) : (
          <label className="saved-value">
            {!params.row.items.length
              ? '-'
              : getDisplayItems(params.row, expandedRows).map((item) => (
                  <div className={styles.taskItems} key={`:${item.id}`}>
                    <div className={styles.taskItems}>{item?.count || '-'}</div>
                  </div>
                ))}
          </label>
        );
      },
    },
    {
      field: 'remarks',
      headerName: 'Remark',
      minWidth: 270,
      flex: 0.95,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) =>
        params.row.isEditing ? (
          <div>
            {params.row.items.map((item) => (
              <TextField
                key={`remarks-${item.id}`}
                defaultValue={item.remarks}
                onChange={(e) =>
                  handleInputChange(e, params.row.id, item.id, 'remarks')
                }
                onKeyDown={(e) => e.stopPropagation()}
                className={styles.remarksTextfield}
                variant="outlined"
                size="small"
              />
            ))}
          </div>
        ) : (
          <label className="saved-value">
            {!params.row.items.length
              ? '-'
              : getDisplayItems(params.row, expandedRows).map((item, index) => (
                  <div className={styles.taskItems} key={index + 1}>
                    {item?.remarks || '-'}
                  </div>
                ))}
          </label>
        ),
    },
    {
      headerClassName: styles.actionColumn,
      cellClassName: styles.actionCell,
      disableColumnMenu: true,
      sortable: false,
      maxWidth: 70,
      renderCell: (params) => (
        <div className={styles.mobileActionIcon}>
          {params.row?.items?.length != 0 ? (
            <>
              {!params.row?.isEditing ? (
                <button
                  className="role-action-edit-icon"
                  disabled={!hasAccess('update')}
                  aria-label="Edit"
                  style={{
                    padding: 0,
                    background: 'none',
                    border: 'none',
                    cursor: hasAccess('update') ? 'pointer' : 'not-allowed',
                    opacity: hasAccess('update') ? 1 : 0.5,
                  }}
                  onClick={() => handleEdit(params.row.id)}
                >
                  <img src={EditIcon} alt="Edit" />
                </button>
              ):(
                <div className={styles['save-and-cancel-button-main']} style={{ paddingTop: '0px' }}>
                <button
                  className={styles['save-and-cancel-button'] + ' ' + styles['save-button-tick-placing']}
                  style={{
                    backgroundColor: '#6B5CE8',
                    color: '#ffff',
                  }}
                  onClick={() => handleSave(params.row.id)}
                  disabled={isRowUnchanged(params.row)}
                >✔</button>
                <button
                  className={styles['save-and-cancel-button'] + ' ' + styles['save-button-cross-placing']}
                  style={{
                    backgroundColor: 'transparent',
                    color: '#6B5CE8',
                    border: '1px solid #6B5CE8'
                  }}
                  onClick={() => handleCancel(params.row.id)}
                >X</button>
              </div>
              )}
            </>
          ) : (
            <button
              className="role-action-edit-icon"
              aria-label="Edit"
              disabled={true}
              style={{
                padding: 0,
                background: 'none',
                cursor: 'not-allowed',
                border: 'none',
              }}
            >
              <img src={DisabledEditIcon} alt="Edit" />
            </button>
          )}
          {params.row?.items?.length > 1 && !params.row?.isEditing && (
            <button
              className="role-action-edit-icon"
              aria-label="Collapse"
              style={{
                padding: 0,
                background: 'none',
                border: 'none',
              }}
              onClick={() => handleCollapse(params.row.id)}
            >
              <img
                src={collapseIcon}
                alt="Edit"
                style={{
                  transform: expandedRows[params.row.id]
                    ? 'rotate(180deg)'
                    : 'none',
                }}
              />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className={styles.skillsetMainDiv}>
      <div className={styles.skillsetSummaryDiv2}>
        <div className={styles.skillsetMain}>
          <span>Required Skillset</span>
          <div className={styles.skillsetSearchAdd}>
            <div className={styles.SkillsetSearch}>
              <img src={MagnifyingGlass} alt="Search" />
              <input
                id="skillset-cost-search-input"
                type="text"
                placeholder="Search..."
                onChange={handleSearch}
              />
            </div>
          </div>
        </div>
      </div>
      <div className={styles.skillsetCostGrid}>
        <DataGridComponent
          dataRows={filteredRows}
          dataColumns={columns}
          pageSize={10}
          isEdit={setRows}
          sortFilterColumns={[]}
          isFilteredRows={filteredRows}
          onRowSelectionChange={() => {}}
          checkboxDisabled={false}
          rowHeight={150}
          noDataImage={NoDataFound}
          noDataTitle="No Skillset added yet."
          noDataSubTitle="Please add it to view here"
          hideSeparator={true}
          editStateHeight={110}
          dataGridCss={styles.dataGrid}
          getRowHeight={() => 'auto'}
        />
      </div>
    </div>
  );
};

export default Skillset;
